import React from 'react'
import { useDispatch, useSelector } from 'react-redux';
import SharePanel from "./SharePanel"
import { BackgroundPanel, Button, Panel, TableAdvanced } from 'vr-react-components';
import { setNotification } from '../store/slice/privateSlice';
import { acceptContributor, acceptInvitation, acceptViewer, declineInvitation, declineRequest } from './SocketConnection';
import { handleSendInvitation, handleSendJoiningRequest } from '../handlers/TroubleshootingPanelHandlers';
import { ACCEPT, ACCEPT_CONTRIBUTOR, ACCEPT_VIEWER, CANCEL, CONTRIBUTOR, DECLINE, INVITE_CONTRIBUTOR, INVITE_VIEWER, JOINING_REQUEST, VIEWER } from '../store/constants/sharedModeFields';

const TroubleshootingPanel = ({ position, scale }) => {
    const dispatch = useDispatch();

    const myUserData = useSelector((state) => state.user.data);
    const device = useSelector((state) => state.private.device);
    const activeRooms = useSelector((state) => state.room.activeRooms);
    const activeUsers = useSelector((state) => state.user.activeUsers);
    const room = useSelector((state) => state.room)

    const activeUserHasActiveRoom = (userSocketId) => {
        return activeRooms.find(activeRoom =>
            activeRoom.roomCreator.socketId === userSocketId
        )
    }

    const activeUserIsBusy = (userSocketId) => {
        return activeRooms.some(activeRoom =>
            activeRoom.participants.some((participant) => participant.socketId === userSocketId)
        )
    }

    const pendingRequestsList = [
        ['Username', 'Date', 'Type'],
        ...room.pendingRequests.map((pr) => { return [pr.username, pr.date, pr.type] }),
    ]

    const width = device.inVRMode ? 6 : 2.5;
    const height = device.inVRMode ? 0.4 : 0.15;
    let c = -1

    return (
        <a-entity position={position} scale={scale}>
            <a-entity position={device.inVRMode ? "-1.5 0.6 -2.65" : "-0.8 1.065 -1.2"}>
                <SharePanel
                    width={width / 3}
                    height={height * 3}
                    roomId={room.roomId}
                />
            </a-entity>

            <a-entity position={device.inVRMode ? "1.1 1.49 -2.66" : "0.45 1.58 -1.21"}>
                <a-text
                    value={"Active Users"}
                    width={device.inVRMode ? 3.8 : 2}
                    height={device.inVRMode ? 3.8 : 2}
                    position={device.inVRMode ? "-1.35 1.33 0.01" : "-0.7 0.67 0"}
                />
                <BackgroundPanel
                    width={device.inVRMode ? 2.9 : 1.5}
                    height={device.inVRMode ? 3 : 1.5}
                >
                    {
                        activeUsers.length ? activeUsers.map((activeUser, i) => {
                            const hasActiveRoom = activeUserHasActiveRoom(activeUser.socketId)
                            const isBusy = hasActiveRoom || activeUserIsBusy(activeUser.socketId)
                            const weAreInTheSameRoom = room ? room.participants.find(participant => participant.socketId === activeUser.socketId) : false
                            const invitationSent = room.myInvitations.some(pr => pr.socketId === activeUser.socketId)
                            const requestToJoinSent = room.myJoiningRequest === hasActiveRoom?.roomId

                            if (!weAreInTheSameRoom) c++;

                            return !weAreInTheSameRoom && (
                                <a-entity key={i} position={
                                    device.inVRMode ?
                                        `-0.25 ${0.85 - c * (height + 0.05)} 0.01` :
                                        `-0.25 ${0.4 - c * (height + 0.05)} 0`
                                }>
                                    <Panel
                                        panel
                                        width={device.inVRMode ? width / 2.5 : width / 2.2}
                                        height={device.inVRMode ? height / 1.2 : height}
                                        backgroundColor="#262626"
                                        aframeClasses={device.canModify ? "collidable" : ""}
                                        position={"0.23 0.05 0.01"}
                                        scale={device.inVRMode ? "1.1 1.1 1" : "1.15 1.15 1"}
                                    >
                                        <a-circle
                                            radius={height * 0.125}
                                            color={hasActiveRoom ? "#00ABD6" : isBusy ? "#E64C21" : "#A0FF24"}
                                            position={device.inVRMode ? "-1.2 -0 0" : "-0.57 0 0"}
                                        >
                                        </a-circle>
                                        <a-text
                                            value={activeUser.username}
                                            height={device.inVRMode ? 1.8 : 0.9}
                                            width={device.inVRMode ? 1.8 : 0.9}
                                            position={device.inVRMode ? "-0.85 0 0.01" : "-0.4 0 0"}
                                        />
                                        <a-image
                                            height={device.inVRMode ? 0.17 : 0.08}
                                            width={device.inVRMode ? 0.17 : 0.08}
                                            position={device.inVRMode ? "-0.98 0.003 0.001" : "-0.47 0.003 0.001"}
                                            src={activeUser.userAgent.toLowerCase().includes("oculus") ? "#Oculus" : "#Windows"}
                                        />
                                        {
                                            <Button
                                                text={requestToJoinSent ? "Request Sent" : hasActiveRoom ? "Request to join" : isBusy ? "In Another Room" : "No Active Room"}
                                                type={Button.constants.primary}
                                                disabled={!hasActiveRoom || requestToJoinSent}
                                                onClickHandler={() => handleSendJoiningRequest({
                                                    joinerUsername: myUserData.username,
                                                    joinerSocketId: myUserData.socketId,
                                                    roomCreatorSocketId: hasActiveRoom.roomCreator.socketId
                                                }, hasActiveRoom.roomId)}
                                                aframeClasses={"collidable"}
                                                width={width / 9}
                                                height={height / 2}
                                                position={device.inVRMode ? "0.02 0 0.01" : "0.06 0 0.01"}
                                                textWidth={width / 5}
                                                textSizeDecimal={device.inVRMode ? 25 : 50}
                                            />
                                        }
                                        {
                                            room?.roomId && <Button
                                                text={invitationSent ? "Invitation Sent" : "Invite"}
                                                type={Button.constants.danger}
                                                disabled={invitationSent}
                                                onClickHandler={() => {
                                                    dispatch(setNotification({
                                                        text: "Want you invite " + activeUser.username + " as:",
                                                        buttons: [INVITE_CONTRIBUTOR, INVITE_VIEWER, CANCEL],
                                                        buttonTypes: ['primary', 'primary', 'danger'],
                                                        data: {
                                                            inviterUsername: myUserData.username,
                                                            inviterSocketId: myUserData.socketId,
                                                            joinerSocketId: activeUser.socketId,
                                                            roomId: room.roomId
                                                        },
                                                        buttonFunctions: (key, data) => {
                                                            switch (key) {
                                                                case INVITE_CONTRIBUTOR:
                                                                    handleSendInvitation(data, CONTRIBUTOR)
                                                                    break;
                                                                case INVITE_VIEWER:
                                                                    handleSendInvitation(data, VIEWER)
                                                                    break;
                                                                case CANCEL:
                                                                    dispatch(setNotification(false))
                                                                    break;

                                                                default:
                                                                    console.log('not yet implemented');
                                                                    break;
                                                            }
                                                        }
                                                    }))
                                                }}
                                                aframeClasses={"collidable"}
                                                width={width / 9}
                                                height={height / 2}
                                                position={device.inVRMode ? "0.75 0 0.01" : "0.36 0 0.01"}
                                                textWidth={width / 5}
                                                textSizeDecimal={device.inVRMode ? 25 : 50}
                                            />
                                        }
                                    </Panel>
                                </a-entity>
                            )
                        }) : <a-text
                            value="No active room to display..."
                            width={device.inVRMode ? 2.7 : 1}
                            height={device.inVRMode ? 2.7 : 1}
                            position={device.inVRMode ? "-0.75 0.05 0" : "-0.3 0.05 0"}
                        />
                    }
                </BackgroundPanel>
            </a-entity>

            <a-entity position={device.inVRMode ? "-1.504 1.87 -2.66" : "-0.8 1.62 -1.2"}>
                <a-text
                    value="Pending Request"
                    width={device.inVRMode ? 2 : 1.3}
                    height={device.inVRMode ? 2 : 1.3}
                    position={device.inVRMode ? "-0.89 0.48 0.01" : "-0.38 0.235 0"}
                />
                <BackgroundPanel
                    width={device.inVRMode ? 2.01 : 0.835}
                    height={device.inVRMode ? 1.2 : 0.6}
                >
                    {
                        room.pendingRequests.length ? <TableAdvanced
                            width={device.inVRMode ? 1.55 : 0.78}
                            height={device.inVRMode ? 0.9 : 0.4}
                            textSizeDecimal={device.inVRMode ? 28 : 50}
                            backgroundColor={"#000000"}
                            headerColor={"#121212"}
                            aframeClasses={"collidable"}
                            disabled={false}
                            position={device.inVRMode ? "0 0.35 0" : "0 0.17 0"}
                            data={pendingRequestsList}
                            onRowClick={(i) => {
                                if (room.pendingRequests[i].type === JOINING_REQUEST)
                                    dispatch(setNotification({
                                        text: "What do you want to do with " + room.pendingRequests[i].username + "'s request",
                                        buttons: [ACCEPT_CONTRIBUTOR, ACCEPT_VIEWER, DECLINE],
                                        buttonTypes: ['primary', 'primary', 'danger'],
                                        data: { joinerSocketId: room.pendingRequests[i].socketId },
                                        buttonFunctions: (key, data) => {
                                            switch (key) {
                                                case ACCEPT_CONTRIBUTOR:
                                                    acceptContributor(data)
                                                    break;
                                                case ACCEPT_VIEWER:
                                                    acceptViewer(data)
                                                    break;
                                                case DECLINE:
                                                    declineRequest(data)
                                                    break;

                                                default:
                                                    console.log('not yet implemented');
                                                    break;
                                            }
                                        }
                                    }))
                                else
                                    dispatch(setNotification({
                                        text: room.pendingRequests[i].username + " invite you in his room:",
                                        buttons: [ACCEPT, DECLINE],
                                        buttonTypes: ['primary', 'danger'],
                                        data: { inviterSocketId: room.pendingRequests[i].socketId, roomId: room.roomId },
                                        buttonFunctions: (key, data) => {
                                            switch (key) {
                                                case ACCEPT:
                                                    acceptInvitation(data)
                                                    break;
                                                case DECLINE:
                                                    declineInvitation(data)
                                                    break;

                                                default:
                                                    console.log('not yet implemented');
                                                    break;
                                            }
                                        }
                                    }))
                            }}
                        /> : <a-text
                            value="No Pending Request..."
                            width={device.inVRMode ? 1.8 : 0.9}
                            height={device.inVRMode ? 1.8 : 0.9}
                            position={device.inVRMode ? "-0.89 0 0.02" : "-0.2 -0.03 0.01"}
                        />

                    }
                </BackgroundPanel>
            </a-entity>
        </a-entity>
    )
}

export default TroubleshootingPanel
